import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Divider,
  Box,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import {
  DarkMode as DarkModeIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  ChevronRight as ChevronRightIcon,
  ExitToApp as LogoutIcon,
} from "@mui/icons-material";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../AuthContext";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../contexts/NotificationContext";
import { useLocale } from "../../contexts/LocaleContext";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../firebase";

const SettingsPage: React.FC = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { locale, setLocale, t } = useLocale();

  // Persisted Notification States
  const [emailNotifications, setEmailNotifications] = useState(() => {
    return localStorage.getItem("settings_email_notifications") !== "false";
  });
  const [pushNotifications, setPushNotifications] = useState(() => {
    return localStorage.getItem("settings_push_notifications") !== "false";
  });

  // Dialog States
  const [securityDialogOpen, setSecurityDialogOpen] = useState(false);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(
      "settings_email_notifications",
      String(emailNotifications)
    );
  }, [emailNotifications]);

  useEffect(() => {
    localStorage.setItem(
      "settings_push_notifications",
      String(pushNotifications)
    );
  }, [pushNotifications]);

  const handleLanguageChange = (event: SelectChangeEvent) => {
    setLocale(event.target.value as "ja" | "en");
    showSuccess(t("common.save"));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const handlePasswordReset = async () => {
    if (currentUser?.email) {
      try {
        await sendPasswordResetEmail(auth, currentUser.email);
        showSuccess(
          `パスワード再設定メールを ${currentUser.email} に送信しました。`
        );
        setSecurityDialogOpen(false);
      } catch (error) {
        console.error("Password reset error", error);
        showError("メールの送信に失敗しました。");
      }
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ mb: 4, fontWeight: "bold" }}
      >
        {t("settings.title")}
      </Typography>

      {/* Account Section */}
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mb: 1, ml: 1 }}
      >
        {t("settings.account")}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <List disablePadding>
          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate("/profile")}>
              <ListItemIcon>
                <PersonIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={t("settings.profile")}
                secondary="個人情報、表示名の変更"
              />
              <ChevronRightIcon color="action" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem disablePadding>
            <ListItemButton onClick={() => setSecurityDialogOpen(true)}>
              <ListItemIcon>
                <SecurityIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={t("settings.security")}
                secondary="パスワードの変更"
              />
              <ChevronRightIcon color="action" />
            </ListItemButton>
          </ListItem>
        </List>
      </Paper>

      {/* Appearance & General Section */}
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mb: 1, ml: 1 }}
      >
        {t("settings.appearance")}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <List disablePadding>
          <ListItem>
            <ListItemIcon>
              <DarkModeIcon />
            </ListItemIcon>
            <ListItemText
              primary={t("settings.darkMode")}
              secondary="画面を暗くして目の疲れを軽減します"
            />
            <ListItemSecondaryAction>
              <Switch
                edge="end"
                checked={darkMode}
                onChange={toggleDarkMode}
                color="primary"
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon>
              <LanguageIcon />
            </ListItemIcon>
            <ListItemText primary={t("settings.language")} />
            <ListItemSecondaryAction sx={{ minWidth: 120 }}>
              <FormControl fullWidth size="small" variant="standard">
                <Select
                  value={locale}
                  onChange={handleLanguageChange}
                  disableUnderline
                >
                  <MenuItem value="ja">日本語</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </FormControl>
            </ListItemSecondaryAction>
          </ListItem>
        </List>
      </Paper>

      {/* Notifications Section */}
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mb: 1, ml: 1 }}
      >
        {t("settings.notifications")}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <List disablePadding>
          <ListItem>
            <ListItemIcon>
              <NotificationsIcon />
            </ListItemIcon>
            <ListItemText
              primary={t("settings.push")}
              secondary="新着メッセージやステータス更新を通知"
            />
            <ListItemSecondaryAction>
              <Switch
                edge="end"
                checked={pushNotifications}
                onChange={(e) => setPushNotifications(e.target.checked)}
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon>
              <NotificationsIcon color="action" />
            </ListItemIcon>
            <ListItemText
              primary={t("settings.email")}
              secondary="重要な更新をメールで受け取る"
            />
            <ListItemSecondaryAction>
              <Switch
                edge="end"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
              />
            </ListItemSecondaryAction>
          </ListItem>
        </List>
      </Paper>

      {/* About Section */}
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mb: 1, ml: 1 }}
      >
        {t("settings.about")}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <List disablePadding>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setAboutDialogOpen(true)}>
              <ListItemIcon>
                <InfoIcon />
              </ListItemIcon>
              <ListItemText
                primary={t("settings.aboutApp")}
                secondary="バージョン 1.0.0"
              />
              <ChevronRightIcon color="action" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary={t("settings.logout")}
                primaryTypographyProps={{ color: "error" }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Paper>

      <Box sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}>
        <Typography variant="caption">
          © 2024 Arcana Inc. All rights reserved.
        </Typography>
      </Box>

      {/* Security Dialog */}
      <Dialog
        open={securityDialogOpen}
        onClose={() => setSecurityDialogOpen(false)}
      >
        <DialogTitle>{t("settings.security")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            パスワードを変更するには、登録済みのメールアドレス (
            {currentUser?.email}) に再設定用のリンクを送信します。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecurityDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handlePasswordReset} autoFocus>
            送信
          </Button>
        </DialogActions>
      </Dialog>

      {/* About Dialog */}
      <Dialog open={aboutDialogOpen} onClose={() => setAboutDialogOpen(false)}>
        <DialogTitle>Arcanaについて</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Arcanaは、先進的な落とし物管理システムです。
            <br />
            <br />
            バージョン: 1.0.0
            <br />
            開発: Arcana Team
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAboutDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettingsPage;
